// Document Center — in-memory mock store. No network (DOCUMENT_CENTER_HANDOFF §7).
import { useSyncExternalStore } from 'react';
import type { Card, CouldntReason, Check, Doc, DocGroupKey } from './types';
import {
  buildCards,
  buildChecks,
  buildDocuments,
  buildMissingChecks,
  DC_CARD_MONTH,
} from './fixtures';
import {
  buildChargedHistory,
  buildCheckHistory,
  buildFiledHistory,
  buildMissingHistory,
} from './historyMock';
import { DC_ACTOR } from './utils';
import { MOCK_NOW } from '@/components/lists';
import { PMS } from '@/features/shared/pms';

export const WORK_MS = 1200;

type Scenario = '' | 'no-fax' | 'couldnt-charge' | 'card-expired' | 'no-statement';

interface State {
  docs: Doc[];
  cards: Card[];
  scenario: Scenario;
  /** Your card processor's rate, in percent (Settings › Virtual cards). */
  cardRate: number;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (raw === 'no-fax' || raw === 'couldnt-charge' || raw === 'card-expired' || raw === 'no-statement') return raw;
  } catch {
    /* no window */
  }
  return '';
}

export const DEFAULT_CARD_RATE = 3.0;

function initState(): State {
  const scenario = readScenario();
  const docs = buildDocuments();
  const cards = buildCards();
  if (scenario === 'couldnt-charge') {
    const c = cards.find((x) => x.id === 'CARD-AMERITAS-412');
    if (c) applyCouldntCharge(c, { reason: 'used', by: DC_ACTOR, markedAt: '9:04 AM', callAt: '9:10 AM' });
  }
  if (scenario === 'card-expired') {
    // An expired card nobody charged follows the same path; ELVA marks it itself.
    const c = cards.find((x) => x.id === 'CARD-AMERITAS-412');
    if (c) applyCouldntCharge(c, { reason: 'expired_unused', by: 'ELVA', markedAt: '9:04 AM', callAt: '9:10 AM' });
  }
  return { docs, cards, scenario, cardRate: DEFAULT_CARD_RATE };
}

const state: State = initState();

// ---- subscription ----
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function useDcVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

export function getScenarioSync(): Scenario {
  return state.scenario;
}
export function faxSetUp(): boolean {
  return state.scenario !== 'no-fax';
}
export function hasStatement(): boolean {
  return state.scenario !== 'no-statement';
}

// ---- history caches (D148 §9) — generated once, deterministic ----
let filedHistoryCache: Doc[] | null = null;
let chargedHistoryCache: Card[] | null = null;
let checkHistoryCache: Check[] | null = null;
let missingHistoryCache: Check[] | null = null;

const MOCK_TODAY_ISO = MOCK_NOW.toISOString().slice(0, 10);

/** The big Documents › Filed history: the live store's filed docs, then the mock. */
export function getFiledHistorySync(): Doc[] {
  filedHistoryCache ??= buildFiledHistory();
  const liveFiled = state.docs.filter((d) => d.group === 'filed' && d.receivedIso);
  return [...liveFiled, ...filedHistoryCache];
}
/** The big Virtual cards › Charged history: live charged cards first, then the mock. */
export function getChargedHistorySync(): Card[] {
  chargedHistoryCache ??= buildChargedHistory();
  const liveCharged = state.cards
    .filter((c) => c.status === 'charged')
    .map((c) => ({ ...c, chargedIso: c.chargedIso ?? MOCK_TODAY_ISO }));
  return [...liveCharged, ...chargedHistoryCache];
}
/** The big Checks history. */
export function getCheckHistorySync(): Check[] {
  checkHistoryCache ??= buildCheckHistory();
  return checkHistoryCache;
}
/** Missing checks, pinned across every month (§9). */
export function getMissingHistorySync(): Check[] {
  missingHistoryCache ??= buildMissingHistory();
  return missingHistoryCache;
}
export function getCheckByIdSync(id: string): Check | null {
  return (
    getCheckHistorySync().find((c) => c.id === id) ??
    getMissingHistorySync().find((c) => c.id === id) ??
    null
  );
}

// ---- documents ----
export function getDocsSync(): Doc[] {
  return state.docs;
}
export function getDocSync(id: string): Doc | null {
  return (
    state.docs.find((d) => d.id === id) ??
    getFiledHistorySync().find((d) => d.id === id) ??
    null
  );
}
export function getDocsByGroup(group: DocGroupKey): Doc[] {
  return state.docs.filter((d) => d.group === group);
}
/**
 * Needs you, in the order the list shows it (D250): a held document with a deadline sorts first
 * (soonest deadline first), then everything else in its original order.
 */
export function getNeedsSorted(): Doc[] {
  const needs = state.docs.filter((d) => d.group === 'needs' && d.status === 'needs');
  const dl = (d: Doc): string => d.held?.deadline?.iso ?? '';
  return needs
    .map((d, i) => ({ d, i }))
    .sort((a, b) => {
      const ha = dl(a.d) !== '';
      const hb = dl(b.d) !== '';
      if (ha !== hb) return ha ? -1 : 1;
      if (ha && hb && dl(a.d) !== dl(b.d)) return dl(a.d) < dl(b.d) ? -1 : 1;
      return a.i - b.i;
    })
    .map((x) => x.d);
}
export function needsCountSync(): number {
  const live = state.docs.filter((d) => d.group === 'needs' && d.status === 'needs').length;
  return live;
}
/** The first document to open: first Needs-you, else the first Today. */
export function firstDocId(): string | null {
  const needs = getNeedsSorted()[0];
  if (needs) return needs.id;
  return state.docs.find((d) => d.group === 'today')?.id ?? state.docs[0]?.id ?? null;
}

// ---- cards ----
export function getCardsSync(): Card[] {
  return state.cards;
}
export function getCardSync(id: string): Card | null {
  return (
    state.cards.find((c) => c.id === id) ??
    getChargedHistorySync().find((c) => c.id === id) ??
    null
  );
}
export function cardsByStatus(status: Card['status']): Card[] {
  return state.cards.filter((c) => c.status === status);
}
export function cardsToChargeCount(): number {
  return cardsByStatus('to_charge').length;
}
export function cardsToChargeTotal(): number {
  return cardsByStatus('to_charge').reduce((s, c) => s + c.amount, 0);
}
export function cardsCouldntChargeCount(): number {
  return cardsByStatus('couldnt_charge').length;
}

// ---- your card processor's rate (Settings › Virtual cards) ----
export function getCardRate(): number {
  return state.cardRate;
}
export function setCardRate(rate: number): void {
  state.cardRate = rate;
  emit();
}
/** What a card payment costs at the saved rate. Computed, never typed. */
export function cardFeeFor(amount: number, rate: number = state.cardRate): number {
  return Math.round(amount * rate) / 100;
}
/** The month's card fees at the saved rate, from the month's card total. */
export function monthCardFees(): number {
  return cardFeeFor(DC_CARD_MONTH.total);
}

// ---- checks ----
export function getChecksSync(): Check[] {
  return buildChecks();
}
export function getMissingChecksSync(): Check[] {
  return buildMissingChecks();
}

// ---- sidebar count (D204) = the band's "Needs you" number, nothing else ----
export function useDocCenterCount(): number {
  return useSyncExternalStore(subscribe, needsCountSync, needsCountSync);
}

// ---- flows ----
function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Needs-you: the person typed the unreadable values; ELVA posts it. */
export async function* answerDocument(id: string, values: Record<string, string>): AsyncGenerator<{ working: string } | { done: Doc }> {
  const doc = state.docs.find((d) => d.id === id);
  if (!doc) return;
  yield { working: 'Saving the amounts and checking them against the check total…' };
  await wait(WORK_MS);
  yield { working: `Posting in ${PMS}…` };
  await wait(WORK_MS);
  doc.status = 'done';
  doc.wentToLabel = 'ELVA posted it · Posting';
  delete doc.tries;
  delete doc.fields;
  delete doc.constraint;
  delete doc.waitForClean;
  // reflect the entered values in "what ELVA read"
  const d1110 = values.d1110 ?? '';
  const d0274 = values.d0274 ?? '';
  doc.read = doc.read
    .filter((r) => r.label !== 'Unreadable')
    .concat([{ label: 'Adam Ross · D1110', value: `$${d1110}` }, { label: 'Adam Ross · D0274', value: `$${d0274}` }]);
  doc.doneTone = 'done';
  doc.doneTitle = 'ELVA posted the EOB';
  doc.doneSteps = [
    { time: 'now', text: `used the two amounts you read: D1110 $${d1110}, D0274 $${d0274}.` },
    { time: 'now', text: `posted all three lines in ${PMS} and filed the EOB.` },
    { time: 'now', text: 'closed the Human in the loop item.' },
  ];
  doc.doneLinks = [{ label: 'See the posting in Posting', href: '/posting' }];
  doc.history = `${doc.history} · read by ${DC_ACTOR}`;
  emit();
  yield { done: doc };
}

/**
 * A held document (unknown sender): Read it (this once) · Read it and trust this sender · Discard (D180, D184).
 * Read moves it to Today while ELVA reads it; Discard removes it. Nothing was read before this.
 */
export function resolveHeld(id: string, action: 'read' | 'trust' | 'discard'): void {
  const doc = state.docs.find((d) => d.id === id);
  if (!doc?.held) return;
  if (action === 'discard') {
    state.docs = state.docs.filter((d) => d.id !== id);
    emit();
    return;
  }
  doc.held.resolved = action === 'read' ? 'read' : 'trust';
  doc.group = 'today';
  doc.status = 'progress';
  doc.wentToLabel = action === 'read' ? 'ELVA is reading it' : 'ELVA is reading it · sender trusted';
  doc.history = `${doc.history} · ${action === 'read' ? 'read once' : 'sender trusted'} by ${DC_ACTOR}`;
  emit();
}

/** This isn't right: ELVA undoes, applies the correction, re-runs. */
export async function* correctDocument(id: string, _correction: string): AsyncGenerator<{ working: string } | { done: Doc }> {
  const doc = state.docs.find((d) => d.id === id);
  if (!doc) return;
  yield { working: 'Undoing what ELVA did with this document…' };
  await wait(WORK_MS);
  yield { working: 'Applying your correction and running it again…' };
  await wait(WORK_MS);
  emit();
  yield { done: doc };
}

/** Marked as charged: the card keeps only its last 4 digits and the approval code. */
export function markCharged(id: string, approval: string): void {
  const c = state.cards.find((x) => x.id === id);
  if (!c || approval.trim() === '') return;
  c.status = 'charged';
  c.chargedBy = `Charged today by ${DC_ACTOR}`;
  c.chargedApproval = approval.trim();
  c.chargedDate = 'today';
  c.chargedIso = MOCK_TODAY_ISO;
  emit();
}

// ---- couldn't charge it (D200) ----
function timeLabel(d: Date): string {
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${String(h % 12 === 0 ? 12 : h % 12)}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

function referenceFor(c: Card): string {
  const letters = c.payer.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X');
  let h = 7;
  for (let i = 0; i < c.id.length; i += 1) h = (h * 31 + c.id.charCodeAt(i)) % 9000;
  const n = h + 55000;
  return c.id === 'CARD-AMERITAS-412' ? 'AM-55102' : `${letters}-${String(n)}`;
}

function payerSaidFor(payer: string, reason: CouldntReason): string {
  if (reason === 'amount') return `${payer} will send a corrected payment by check within 10 business days. ELVA watches for it.`;
  if (reason === 'expired_unused') return `${payer} will pay by EFT instead, within 10 business days. ELVA watches for it.`;
  return `${payer} will reissue the payment by check within 10 business days. ELVA watches for it.`;
}

interface CouldntArgs { reason: CouldntReason; note?: string; by: string; markedAt: string; callAt: string }

/** Moves a card to Couldn't charge with ELVA's (simulated) call to the payer. */
function applyCouldntCharge(c: Card, a: CouldntArgs): void {
  c.status = 'couldnt_charge';
  c.couldnt = {
    reason: a.reason,
    ...(a.note ? { note: a.note } : {}),
    markedBy: a.by,
    markedAt: a.markedAt,
    callAt: a.callAt,
    reference: referenceFor(c),
    payerSaid: payerSaidFor(c.payer, a.reason),
  };
}

/** "Couldn't charge it" → "Send to ELVA". ELVA calls the payer and asks it to pay another way. */
export function markCouldntCharge(id: string, reason: Exclude<CouldntReason, 'expired_unused'>, note?: string): void {
  const c = state.cards.find((x) => x.id === id);
  if (c?.status !== 'to_charge') return;
  const now = new Date();
  const trimmed = note?.trim();
  applyCouldntCharge(c, {
    reason,
    ...(trimmed ? { note: trimmed } : {}),
    by: DC_ACTOR,
    markedAt: timeLabel(now),
    callAt: timeLabel(new Date(now.getTime() + 6 * 60_000)),
  });
  emit();
}

/**
 * HAND-OFF to Insurance A/R. Every claim a couldn't-charge card was meant to pay,
 * unpaid again, with the payer's reference. Insurance A/R can read this (and
 * re-render on `subscribe` / `useDcVersion`) to list the claims as unpaid.
 */
export interface CouldntChargeClaim {
  cardId: string;
  payer: string;
  reference: string;
  patient: string;
  visit: string;
  amount: number;
  /** What the payer said on ELVA's call. */
  payerSaid: string;
}
export function getCouldntChargeClaims(): CouldntChargeClaim[] {
  return state.cards
    .filter((c) => c.status === 'couldnt_charge' && c.couldnt)
    .flatMap((c) =>
      c.claims.map((cl) => ({
        cardId: c.id,
        payer: c.payer,
        reference: c.couldnt?.reference ?? '',
        patient: cl.patient,
        visit: cl.visit,
        amount: cl.amount,
        payerSaid: c.couldnt?.payerSaid ?? '',
      })),
    );
}
